import { useNavigate, useLocation } from 'react-router-dom';
import { useLocalizedRoutes } from './useLocalizedRoutes';
import { useTranslation } from 'react-i18next';
import { getEquivalentRoute } from '../utils/routeLocalization';

interface NavigationOptions {
  replace?: boolean;
  state?: any;
}

interface UseLocalizedNavigationReturn {
  navigateToRoute: (routeKey: keyof ReturnType<typeof useLocalizedRoutes>['routes'], options?: NavigationOptions) => void;
  navigateWithLang: (path: string, options?: NavigationOptions) => void;
  switchLanguageAndNavigate: (newLanguage: string) => void;
  getCurrentRouteKey: () => string | null;
  currentLanguage: string;
}

/**
 * Custom hook for language-aware navigation
 * Provides navigation methods that automatically use localized routes
 */
export const useLocalizedNavigation = (): UseLocalizedNavigationReturn => {
  const navigate = useNavigate();
  const location = useLocation();
  const { getLocalizedPath, currentLanguage } = useLocalizedRoutes();
  const { i18n } = useTranslation();

  /**
   * Navigate to a localized route
   * @param routeKey - The key of the route from useLocalizedRoutes
   * @param options - Additional navigation options
   */
  const navigateToRoute = (
    routeKey: keyof ReturnType<typeof useLocalizedRoutes>['routes'], 
    options: NavigationOptions = {}
  ): void => {
    const path = getLocalizedPath(routeKey);
    const searchParams = new URLSearchParams(location.search);
    
    // Preserve existing query params and add/update lang param
    searchParams.set('lang', currentLanguage);
    
    const fullPath = `${path}?${searchParams.toString()}`;
    navigate(fullPath, options);
  };

  /**
   * Navigate to a custom path with language parameter
   * @param path - The path to navigate to
   * @param options - Additional navigation options
   */
  const navigateWithLang = (path: string, options: NavigationOptions = {}): void => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('lang', currentLanguage);
    
    const fullPath = `${path}?${searchParams.toString()}`;
    navigate(fullPath, options);
  };

  /**
   * Switch language and navigate to equivalent page
   * @param newLanguage - The new language code
   */
  const switchLanguageAndNavigate = (newLanguage: string): void => {
    // Change the i18n language
    i18n.changeLanguage(newLanguage);
    
    // Store in localStorage
    if (typeof window !== 'undefined') {
      localStorage.setItem('i18nextLng', newLanguage);
    }

    // Get current path and find equivalent in new language
    const currentPath = location.pathname;
    const equivalentPath = getEquivalentRoute(currentPath, currentLanguage, newLanguage);
    
    // Create new search params with updated language
    const searchParams = new URLSearchParams(location.search);
    searchParams.set('lang', newLanguage);
    
    // Navigate to equivalent path with new language
    const newPath = `${equivalentPath}?${searchParams.toString()}`;
    
    navigate(newPath, { replace: true });
  };

  /**
   * Get the current route key based on current pathname
   * @returns The route key or null if not found
   */
  const getCurrentRouteKey = (): string | null => {
    const { routes } = useLocalizedRoutes();
    const currentPath = location.pathname;
    
    for (const [key, path] of Object.entries(routes)) {
      if (path === currentPath) {
        return key;
      }
    }
    return null;
  };

  return {
    navigateToRoute,
    navigateWithLang,
    switchLanguageAndNavigate,
    getCurrentRouteKey,
    currentLanguage
  };
};
